import type { FC, KeyboardEvent, PointerEvent, ReactNode } from 'react'
import type { FlowStep } from '@/constants/pilot'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { Reveal } from '@/components/generals/reveal'
import { AppIcon } from '@/components/generals/app-icon'
import { SlidingNumber } from '@/components/generals/sliding-number'
import { useChat } from '@/components/generals/chat-assistant'
import { useInView } from '@/hooks/use-in-view'
import { useReducedMotion } from '@/hooks/use-reduced-motion'
import { clusterApps, flowSteps, pilotSteps } from '@/constants/pilot'

import {
    ArrowRight,
    ArrowUpDown,
    Check,
    GripVertical,
    Plug,
    Route,
    Sparkles,
    Zap
} from 'lucide-react'

const stepIcons = [Plug, Route, Zap]

const STEP_DURATION = 8000

// Step one: the apps Pilot can see, floating around her.
const AppCluster: FC = (): ReactNode => {
    const positions = clusterApps.map((app, index) => {
        const angle = (index / clusterApps.length) * Math.PI * 2 - Math.PI / 2

        // Rounded so the server and every browser render exactly the same numbers.
        return {
            app,
            left: Math.round((50 + Math.cos(angle) * 37) * 100) / 100,
            top: Math.round((50 + Math.sin(angle) * 34) * 100) / 100
        }
    })

    return (
        <div className='absolute inset-0'>
            <svg aria-hidden='true' viewBox='0 0 100 100' preserveAspectRatio='none' className='absolute inset-0 h-full w-full'>
                {positions.map(position => (
                    <line
                        key={position.app}
                        x1='50'
                        y1='50'
                        x2={position.left}
                        y2={position.top}
                        stroke='rgba(93,45,230,0.18)'
                        strokeWidth='1'
                        strokeDasharray='3 4'
                        vectorEffect='non-scaling-stroke'
                    />
                ))}
            </svg>

            <div className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'>
                <span aria-hidden='true' className='absolute inset-0 -m-4 rounded-full bg-violet/10 animate-pulse' />

                <span className='relative inline-flex size-16 items-center justify-center rounded-full bg-gradient-to-b from-[#7a52ff] to-violet text-white shadow-[0_14px_30px_-10px_rgba(93,45,230,0.7)]'>
                    <Sparkles className='size-7' aria-hidden='true' />
                </span>
            </div>

            {positions.map((position, index) => (
                <div
                    key={position.app}
                    className='absolute -translate-x-1/2 -translate-y-1/2'
                    style={{ left: `${position.left}%`, top: `${position.top}%` }}
                >
                    <div className='animate-float' style={{ animationDelay: `${index * -0.8}s`, animationDuration: `${6 + (index % 3)}s` }}>
                        <span className='inline-flex rounded-[15px] bg-white p-1 shadow-[0_10px_24px_-14px_rgba(14,14,14,0.35)] ring-1 ring-hairline'>
                            <AppIcon name={position.app} size='md' label />
                        </span>
                    </div>
                </div>
            ))}
        </div>
    )
}

const reorder = (list: Array<FlowStep>, from: number, to: number): Array<FlowStep> => {
    const next = [...list]
    const [moved] = next.splice(from, 1)

    next.splice(to, 0, moved)

    return next
}

// Step two: a step-by-step flow the visitor can drag into a new order (or move with the arrow keys).
const FlowEditor: FC = (): ReactNode => {
    const [items, setItems] = useState(flowSteps)
    const [drag, setDrag] = useState<{ index: number, offset: number } | null>(null)
    const [announcement, setAnnouncement] = useState('')
    const [settling, setSettling] = useState(false)
    const listRef = useRef<HTMLOListElement>(null)
    const startY = useRef(0)
    const rowSize = useRef(58)

    const target = drag
        ? Math.max(0, Math.min(items.length - 1, drag.index + Math.round(drag.offset / rowSize.current)))
        : null

    const measure = () => {
        const first = listRef.current?.children[0] as HTMLElement | undefined

        if (first) rowSize.current = first.offsetHeight + 8
    }

    const start = (index: number, event: PointerEvent<HTMLButtonElement>) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return

        measure()
        event.currentTarget.setPointerCapture(event.pointerId)
        startY.current = event.clientY
        setDrag({ index, offset: 0 })
    }

    const move = (event: PointerEvent<HTMLButtonElement>) => {
        if (!drag) return

        const min = -drag.index * rowSize.current
        const max = (items.length - 1 - drag.index) * rowSize.current

        setDrag({ index: drag.index, offset: Math.max(min - 12, Math.min(max + 12, event.clientY - startY.current)) })
    }

    const end = () => {
        if (!drag) return

        if (target !== null && target !== drag.index) {
            setAnnouncement(`Moved "${items[drag.index].text}" to step ${target + 1}.`)
            setItems(reorder(items, drag.index, target))
        }

        // Snap every row into its new place without animating from the old offsets.
        setSettling(true)
        setDrag(null)

        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => setSettling(false))
        })
    }

    const handleKeys = (index: number, event: KeyboardEvent<HTMLButtonElement>) => {
        const direction = event.key === 'ArrowUp' ? -1 : event.key === 'ArrowDown' ? 1 : 0
        const next = index + direction

        if (!direction || next < 0 || next >= items.length) return

        event.preventDefault()
        setAnnouncement(`Moved "${items[index].text}" to step ${next + 1}.`)
        setItems(reorder(items, index, next))

        window.requestAnimationFrame(() => {
            listRef.current?.querySelectorAll<HTMLButtonElement>('[data-grip]')[next]?.focus()
        })
    }

    return (
        <div className='absolute inset-0 flex flex-col justify-center gap-3 px-4 sm:px-8'>
            <p className='inline-flex items-center gap-1.5 text-[12.5px] font-medium text-slate'>
                <ArrowUpDown className='size-3.5' aria-hidden='true' />
                Drag the steps into your order
            </p>

            <ol ref={listRef} className='flex flex-col gap-2'>
                {items.map((item, index) => {
                    const isDragging = drag?.index === index
                    let shift = 0

                    if (drag && target !== null && !isDragging) {
                        if (drag.index < target && index > drag.index && index <= target) shift = -rowSize.current
                        if (drag.index > target && index < drag.index && index >= target) shift = rowSize.current
                    }

                    return (
                        <li
                            key={item.id}
                            className={cn(
                                'relative flex items-center gap-3 rounded-[14px] bg-white py-2 pl-1.5 pr-3 ring-1 ring-hairline',
                                isDragging && 'shadow-[0_18px_34px_-18px_rgba(14,14,14,0.45)] ring-[#cfc2ff]'
                            )}
                            style={{
                                transform: `translateY(${isDragging ? (drag?.offset ?? 0) : shift}px)`,
                                transition: isDragging || settling ? 'none' : 'transform 0.22s var(--fc-ease-out)',
                                zIndex: isDragging ? 10 : 1
                            }}
                        >
                            <button
                                type='button'
                                data-grip
                                aria-label={`Step ${index + 1}: ${item.text}. Use the arrow keys to move it.`}
                                onPointerDown={event => start(index, event)}
                                onPointerMove={move}
                                onPointerUp={end}
                                onPointerCancel={end}
                                onKeyDown={event => handleKeys(index, event)}
                                className={cn('inline-flex h-9 w-7 shrink-0 touch-none items-center justify-center rounded-lg text-[#a3a3ad] transition-colors hover:bg-mist hover:text-ink focus-visible:outline-2 focus-visible:outline-violet', isDragging ? 'cursor-grabbing' : 'cursor-grab')}
                            >
                                <GripVertical className='size-4' aria-hidden='true' />
                            </button>

                            <span className='inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-mist text-[11.5px] font-semibold text-slate'>
                                {index + 1}
                            </span>

                            <AppIcon name={item.app} size='sm' />

                            <span className='min-w-0 truncate text-[13.5px] font-medium text-ink'>
                                {item.text}
                            </span>
                        </li>
                    )
                })}
            </ol>

            <p aria-live='polite' className='sr-only'>
                {announcement}
            </p>
        </div>
    )
}

interface RecentRun {
    id: number
    order: number
    age: number
}

const ageLabel = (seconds: number): string => {
    if (seconds < 2) return 'just now'
    if (seconds < 60) return `${seconds}s ago`

    return `${Math.floor(seconds / 60)}m ago`
}

// Step three: the finished workflow, running live.
const RunningCard: FC<{ active: boolean, reduced: boolean }> = ({ active, reduced }): ReactNode => {
    const [runs, setRuns] = useState(1284)
    const [recent, setRecent] = useState<Array<RecentRun>>([
        { id: 3, order: 48213, age: 4 },
        { id: 2, order: 48212, age: 19 },
        { id: 1, order: 48211, age: 37 }
    ])
    const nextId = useRef(4)

    useEffect(() => {
        if (!active || reduced) return

        const tick = window.setInterval(() => {
            setRecent(previous => previous.map(run => ({ ...run, age: run.age + 1 })))
        }, 1000)

        const runTimer = window.setInterval(() => {
            setRuns(previous => previous + 1)
            setRecent(previous => [{ id: nextId.current++, order: previous[0].order + 1, age: 0 }, ...previous].slice(0, 3))
        }, 4600)

        return () => {
            window.clearInterval(tick)
            window.clearInterval(runTimer)
        }
    }, [active, reduced])

    return (
        <div className='absolute inset-0 flex items-center justify-center p-4 sm:p-6'>
            <div className='w-full max-w-[400px] rounded-[18px] bg-[#effaf3] p-4 ring-1 ring-inset ring-[#cdebd9] shadow-[0_22px_44px_-30px_rgba(5,56,26,0.55)]'>
                <div className='flex items-start justify-between gap-3'>
                    <div className='flex flex-col'>
                        <span className='text-[12px] font-medium text-forest/70'>
                            Workflow
                        </span>

                        <span className='text-[15px] font-semibold tracking-[-0.01em] text-forest'>
                            Wholesale order sync
                        </span>
                    </div>

                    <span className='inline-flex items-center gap-1.5 rounded-full bg-forest px-2.5 py-1 text-[12px] font-semibold text-mint'>
                        <span className='size-1.5 rounded-full bg-[#4ade80] animate-pulse-dot' />
                        Running
                    </span>
                </div>

                <dl className='mt-4 grid grid-cols-3 gap-2'>
                    <div className='rounded-xl bg-white/70 px-3 py-2.5'>
                        <dt className='text-[11.5px] text-forest/70'>Runs today</dt>
                        <dd className='mt-1 text-[17px] font-semibold text-forest'>
                            <SlidingNumber value={runs.toLocaleString('en-US')} play={active} duration={900} />
                        </dd>
                    </div>

                    <div className='rounded-xl bg-white/70 px-3 py-2.5'>
                        <dt className='text-[11.5px] text-forest/70'>Last run</dt>
                        <dd className='mt-1 text-[15px] font-semibold text-forest'>{ageLabel(recent[0].age)}</dd>
                    </div>

                    <div className='rounded-xl bg-white/70 px-3 py-2.5'>
                        <dt className='text-[11.5px] text-forest/70'>Success</dt>
                        <dd className='mt-1 text-[17px] font-semibold text-forest'>99.8%</dd>
                    </div>
                </dl>

                <ul className='mt-3 flex flex-col gap-1.5'>
                    {recent.map(run => (
                        <li key={run.id} className='fc-fade-up flex items-center gap-2.5 rounded-xl bg-white px-3 py-2'>
                            <span className='inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-mint text-forest'>
                                <Check className='size-3' aria-hidden='true' />
                            </span>

                            <span className='min-w-0 flex-1 truncate text-[12.5px] text-ink'>
                                Order #{run.order} synced to NetSuite
                            </span>

                            <span className='shrink-0 text-[11.5px] text-slate'>
                                {ageLabel(run.age)}
                            </span>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    )
}

export const PilotSteps: FC = (): ReactNode => {
    const { ask } = useChat()
    const reduced = useReducedMotion()
    const { ref, inView } = useInView<HTMLElement>({ once: false, rootMargin: '0px', threshold: 0.25 })
    const [active, setActive] = useState(0)
    const [cycle, setCycle] = useState(0)
    const [hovered, setHovered] = useState(false)
    const paused = !inView || hovered || reduced

    const select = (index: number) => {
        setActive(index)
        setCycle(previous => previous + 1)
    }

    const illustrations = [
        <AppCluster key='cluster' />,
        <FlowEditor key='flow' />,
        <RunningCard key='running' active={active === 2 && inView} reduced={reduced} />
    ]

    return (
        <section ref={ref} id='how-it-works' aria-labelledby='how-it-works-title' className='pb-24 md:pb-32'>
            <div className='fc-container'>
                <Reveal className='grid gap-10 rounded-[32px] bg-mist p-5 ring-1 ring-inset ring-[#ececf1] sm:p-10 lg:grid-cols-[minmax(0,0.82fr)_minmax(0,1.18fr)] lg:gap-14 lg:p-14'>
                    <div className='flex flex-col items-start justify-between gap-8 px-1 pt-2 sm:px-0 sm:pt-0'>
                        <h2 id='how-it-works-title' className='max-w-[460px] text-[clamp(1.75rem,3.1vw,2.625rem)] font-medium leading-[1.12] tracking-[-0.03em]'>
                            <span className='text-ink'>Pilot is your personal productivity agent.</span>{' '}
                            <span className='text-silver'>Show her your work, and she will handle the routine.</span>
                        </h2>

                        <button
                            type='button'
                            onClick={() => ask('How does Pilot work?')}
                            className='group inline-flex items-center gap-1.5 rounded text-[15px] font-semibold text-violet transition-colors hover:text-violet-bright focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-violet'
                        >
                            Learn more about Pilot
                            <ArrowRight className='size-4 transition-transform duration-300 group-hover:translate-x-0.5' aria-hidden='true' />
                        </button>
                    </div>

                    <div
                        className='flex flex-col gap-3'
                        onMouseEnter={() => setHovered(true)}
                        onMouseLeave={() => setHovered(false)}
                        onFocusCapture={() => setHovered(true)}
                        onBlurCapture={() => setHovered(false)}
                    >
                        {pilotSteps.map((step, index) => {
                            const isActive = index === active
                            const Icon = stepIcons[index]

                            return (
                                <div
                                    key={step.id}
                                    className={cn(
                                        'relative overflow-hidden rounded-[20px] bg-white ring-1 transition-shadow duration-300',
                                        isActive ? 'shadow-[0_22px_48px_-32px_rgba(54,30,140,0.5)] ring-[#e4defb]' : 'ring-hairline'
                                    )}
                                >
                                    <button
                                        type='button'
                                        id={`pilot-step-trigger-${step.id}`}
                                        aria-expanded={isActive}
                                        aria-controls={`pilot-step-${step.id}`}
                                        onClick={() => select(index)}
                                        className='flex w-full items-start gap-4 p-4 text-left focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-violet sm:p-5'
                                    >
                                        <span className={cn('inline-flex size-10 shrink-0 items-center justify-center rounded-xl transition-colors duration-300', isActive ? 'bg-violet text-white' : 'bg-mist text-slate')}>
                                            <Icon className='size-5' aria-hidden='true' />
                                        </span>

                                        <span className='pt-1 text-[15px] leading-relaxed'>
                                            <strong className='font-semibold text-ink'>{step.lead}</strong>{' '}
                                            <span className='text-slate'>{step.text}</span>
                                        </span>
                                    </button>

                                    <div
                                        id={`pilot-step-${step.id}`}
                                        role='region'
                                        aria-labelledby={`pilot-step-trigger-${step.id}`}
                                        className={cn('grid transition-[grid-template-rows] duration-500 ease-out', isActive ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}
                                    >
                                        <div className='overflow-hidden' inert={!isActive}>
                                            <div className='px-4 pb-4 sm:px-5 sm:pb-5'>
                                                <div className='relative h-[280px] overflow-hidden rounded-[16px] bg-gradient-to-b from-[#f7f5ff] to-[#f2f2f6] ring-1 ring-inset ring-[#ebe8f6] sm:h-[300px]'>
                                                    {illustrations[index]}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {isActive && (
                                        <span aria-hidden='true' className='absolute left-0 top-0 h-full w-[3px] bg-violet/10'>
                                            <span
                                                key={cycle}
                                                className='fc-progress-bar-y block h-full w-full bg-violet'
                                                style={reduced
                                                    ? { animation: 'none', transform: 'scaleY(1)' }
                                                    : { animationDuration: `${STEP_DURATION}ms`, animationPlayState: paused ? 'paused' : 'running' }}
                                                onAnimationEnd={() => select((index + 1) % pilotSteps.length)}
                                            />
                                        </span>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                </Reveal>
            </div>
        </section>
    )
}
